import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { FormlyInputType, FormlyType, GaFormlyService, IExtraFieldConfigurationObject } from '../../shared/formly/ga-formly.service';
import * as _ from 'underscore';
import { util } from '../../shared/util';
import { IObject } from '../../../type_definitions/datatypes';
import { CustomComponentService } from './custom-component.service';
import { Dictionary } from '../../../type_definitions/vendor';
import { ReportFiltersUtil } from '../../shared/report/report-filters-util.service';
import { BaseCustomComponent } from './base-custom.component';

export interface IGAFormItem {
  keyValue?:string | number;
  displayValue?:string | number;
  infoTextValue?:string;
  infoLabelValue?:string;
}

export enum GA3FormVisibility {
  HIDDEN = 'HIDDEN',
  EDITABLE = 'EDITABLE',
  READONLY = 'READONLY',
  INTERNAL = 'INTERNAL'
}

export interface IGAFormField {
  id:string; // model key of formly
  caption?:string; // label of formly
  type?:string; // input type
  visibility?: GA3FormVisibility;
  block?:string;
  component?:string;
  items?:IGAFormItem[]; // to be used in radio field
  data?:IGAFormItem;
  status?:string;
  events?:IGAFormEvent[];
  info?:string;
  key?:boolean; // is primary key
  validation?:IGAFormValidation;
  help?:IGAFormHelper;
}

export interface IGAFormLayout {
  layout:string;
}

export interface IGAFormSection {
  id:string;
  title?:string;
  fields:IGAFormField[];
  bindings?:any;
  style?:{
    collapsible:boolean;
  };
  help?: IGAFormHelper;
}

export interface IGAFormAction {
  id:string;
  type:string;
  caption?:string;
}

export interface IGAFormEvent {
  type:string;
  context?:string;
  source?:string;
  when?:string;
  field?:string;
  data?:{
    filter:string;
  };
}

export interface IGAFormHelper {
  label?:string;
  url?:string;
  text?:string;
  textVisible?:boolean;
}

export interface IGAFormMetadata {
  id:string;
  type?:string;
  module?:string;
  title?:string;
  cookies?: IObject;
  style?:IGAFormLayout;
  sections?:IGAFormSection[];
  actions?:IGAFormAction[];
  text?:string;
  primaryKey?:string;
  status?:string;
  help?:IGAFormHelper;
}

export interface IGAFormActionEventRequest {
  type:string;
  context:{
    fields?:IGAFormField[];
    key?:string;
  };
  callContext?: IGA3CallContext;
  events?:IGAFormEvent[];
  actions?:IGAFormAction[];
}

export interface IGAFormFieldChange extends IGAFormField {
  sectionId:string;
  event?:'UPDATE' | 'DELETE';
}

export interface IGAFormValidation {
  optional?:boolean;
  minLength?:number;
  maxLength?:number;
  min?:number;
  max?:number;
  pattern:'EMAIL' | 'URL' | string | RegExp;
}

export interface IGAFormTypeValue {
  value:string | number;
  type:string;
  key?: boolean;
  visibility?: GA3FormVisibility;
  notEditableWhenFilled?: boolean;
}

export interface IGABrowselistFormTypeValue extends Omit<IGAFormTypeValue, 'value'> {
  valueOrigin: ValueOrigin | ValueOrigin[];
  value: string | number | any;
}

export interface IGA3CallContext {
  type: CallContextType;
  reportId: number;
  selectedKeys: string[] | number[];
}

export enum CallContextType {
  REPORT = 'REPORT'
}

export enum ActionButtonClass {
  'DELETE-ACTION' = 'btn-danger',
  'NEW-ACTION' = 'btn-success',
  'SAVE-ACTION' = 'btn-success',
  'REFRESH-ACTION' = 'btn-info',
  'CANCEL-ACTION' = 'btn-danger'
}

export enum ResponseType {
  MESSAGE = 'MESSAGE',
  FORM = 'FORM'
}

export enum FieldStatus {
  UNLOADED = 'UNLOADED',
  LOADING = 'LOADING',
  LOADED = 'LOADED'
}

export enum ValueOrigin {
  REPORT_FILTERS = 'reportFilters',
  COMPONENT_CONTEXT = 'componentContext',
  REPORT_ROW_VALUE = 'reportRowValue',
  FIXED = 'fixed'
}

export const EVENT_INIT_ACTION:string = 'INIT-ACTION';
export const EVENT_DELETE_ACTION:string = 'DELETE-ACTION';
export const EVENT_NEW_ACTION:string = 'NEW-ACTION';
export const EVENT_SAVE_ACTION:string = 'SAVE-ACTION';
export const EVENT_LOAD_ACTION:string = 'LOAD-ACTION';
export const EVENT_CANCEL_ACTION:string = 'CANCEL-ACTION';
export const EVENT_CUSTOM_ACTION:string = 'CUSTOM';

export const TYPE_DELETE:string = 'DELETE';
export const TYPE_UPDATE:string = 'UPDATE';
export const TYPE_INIT:string = 'INIT';
export const TYPE_LOAD:string = 'LOAD';
export const EVENT_REQUEST_ITEMS:string = 'REQUEST_ITEMS';
export const EVENT_ON_CHANGE:string = 'ON_CHANGE';

export const FIELD_TYPE_SEARCH:string = 'SEARCH';
export const FIELD_TYPE_SELECT:string = 'SELECT';
export const FIELD_TYPE_SELECT2:string = 'SELECT2';
export const FIELD_TYPE_MULTISELECT:string = 'MULTISELECT';
export const FIELD_TYPE_NGSELECT:string = 'NGSELECT';
export const FIELD_TYPE_INPUT:string = 'INPUT';
export const FIELD_TYPE_NUMBER:string = 'NUMBER';
export const FIELD_TYPE_DATEINPUT:string = 'DATEINPUT';
export const FIELD_TYPE_RADIO:string = 'RADIO';
export const FIELD_TYPE_TEXTAREA:string = 'TEXTAREA';
export const FIELD_TYPE_CHECKBOX:string = 'CHECKBOX';
export const FIELD_TYPE_VALUE:string = 'VALUE';
export const FIELD_TYPE_RADIOBUTTON:string = 'RADIOBUTTON';
export const FIELD_TYPE_TITLE:string = 'TITLE';
export const FIELD_TYPE_CUSTOM:string = 'CUSTOM';
export const FIELD_TYPE_EMAIL:string = 'EMAIL';
export const FIELD_TYPE_URL:string = 'URL';

const labelClass:string = 'col-sm-4 col-md-3';
const valueClass:string = 'col-sm-8 col-md-9';

@Injectable()
export class FormsService {
  public fieldsOnChange:Observable<IGAFormFieldChange>;
  private _field$:BehaviorSubject<IGAFormFieldChange>;

  private readonly keyValueFields:string[] = [
    FIELD_TYPE_SELECT,
    FIELD_TYPE_SELECT2,
    FIELD_TYPE_RADIO,
    FIELD_TYPE_SEARCH,
    FIELD_TYPE_MULTISELECT
  ];

  constructor(
    private formlyService:GaFormlyService,
    private customComponentService:CustomComponentService,
    private reportFiltersUtil:ReportFiltersUtil
  ) {
    // register field observable
    this._field$ = <BehaviorSubject<IGAFormFieldChange>>new BehaviorSubject(undefined);
    this.fieldsOnChange = this._field$.asObservable();
  }

  public clearSubject():void {
    this._field$.next(undefined);
  }

  public registerField(field:IGAFormField, customBindings?: BaseCustomComponent, model?: IObject):IExtraFieldConfigurationObject {
    const labelProp:string = 'displayValue';
    const valueProp:string = 'keyValue';
    const fieldType:string = this.getFieldType()[field.type] === FormlyType.NUMBER? FormlyType.INPUT : this.getFieldType()[field.type];

    const newField:IExtraFieldConfigurationObject = this.formlyService.aField(this.isFieldEditable(field)? fieldType : FormlyType.VALUE, <string>field.id, field.caption)
      .withComponent(this.customComponentService.getComponent(field.component))
      .withLabelClass(labelClass)
      .withValueClass(fieldType === FormlyType.CUSTOM? undefined : valueClass)
      .withFormat(labelProp)
      .withOptions(field.items)
      .withInputType(this.getInputType(field.type, <string>field.validation?.pattern))
      .withRequired(field.validation? !field.validation?.optional : false)
      .withMinLength(field.validation?.minLength)
      .withMaxLength(field.validation?.maxLength)
      .withPattern(this.getValidationPattern(field.validation?.pattern))
      .withValueProp(fieldType === FormlyType.RADIO? valueProp : undefined)
      .withHideExpression(() => this.isFieldHidden(field))
      .withExtraOptions({
        templateOptions: {
          trackBy: valueProp,
          min: field.validation?.min,
          max: field.validation?.max,
          showInfo: field.help?.textVisible
        }
      })
      .withInfo(field.help?.text)
      .withValueInfo({
        label: () => model?.[field.id]?.infoLabelValue,
        content: () => model?.[field.id]?.infoTextValue,
        contentType: 'html'
      })
      .build();

    // add extra options based on field type
    switch (newField.type) {
      case FormlyType.CUSTOM:
        newField.templateOptions.inputs = _.extend(customBindings || {}, { getModelValue: () => model?.[field.id], field });
        newField.templateOptions.noWrapper = true;
        break;

      case FormlyType.SELECT:
      case FormlyType.SELECT2:
        // prevent adding initial disabled option to select field
        newField.templateOptions.addInitialOption = false;
        break;

      case FormlyType.SEARCH:
        newField.templateOptions.minSearchLength = 2;
        break;
    }

    return newField;
  }

  private getValidationPattern(pattern:'EMAIL' | 'URL' | string | RegExp):RegExp {
    if (pattern !== FIELD_TYPE_EMAIL && pattern !== FIELD_TYPE_URL) {
      return <RegExp>pattern;
    }
  }

  private getInputType(type:string, pattern?:string):string {
    if (this.getFieldType()[type] === FormlyInputType.NUMBER) {
     return FormlyInputType.NUMBER;
    }
    if (pattern === FIELD_TYPE_EMAIL) {
      return FormlyInputType.EMAIL;
    } else if (pattern === FIELD_TYPE_URL) {
      return FormlyInputType.URL;
    }
  }

  private getFieldType():{ [key:string]:string } {
    return {
      [FIELD_TYPE_NUMBER]: FormlyType.NUMBER,
      [FIELD_TYPE_INPUT]: FormlyType.INPUT,
      [FIELD_TYPE_TEXTAREA]: FormlyType.TEXTAREA,
      [FIELD_TYPE_VALUE]: FormlyType.VALUE,
      [FIELD_TYPE_DATEINPUT]: FormlyType.DATE_INPUT,
      [FIELD_TYPE_RADIO]: FormlyType.RADIO,
      [FIELD_TYPE_RADIOBUTTON]: FormlyType.RADIO_BUTTON,
      [FIELD_TYPE_SELECT]: FormlyType.SELECT,
      [FIELD_TYPE_SELECT2]: FormlyType.SELECT2,
      [FIELD_TYPE_SEARCH]: FormlyType.SEARCH,
      [FIELD_TYPE_MULTISELECT]: FormlyType.MULTI_SELECT,
      [FIELD_TYPE_NGSELECT]: FormlyType.NG_SELECT,
      [FIELD_TYPE_CHECKBOX]: FormlyType.CHECKBOX,
      [FIELD_TYPE_TITLE]: FormlyType.TITLE,
      [FIELD_TYPE_CUSTOM]: FormlyType.CUSTOM
    };
  }

  /**
   * @param key {string | string[]} - key or composite keys
   * @param [context] {IObject} - context of default init model
   * @param [compositeKeyForm] {boolean} - use context as key for composite keys form
   * @param [callContext] {IGA3CallContext} - Call context of ga3 caller
   * @description create init event request so it being handled in one place
   * @return {IGAFormActionEventRequest}
   */
  public initEventRequest(key:string | string[], context?:IObject, compositeKeyForm?: boolean, callContext?: IGA3CallContext):IGAFormActionEventRequest {
    if (compositeKeyForm) {
      return this.createActionRequest(
        undefined,
        context? this.transformContextToSections(context) : undefined,
        [
          {
            id: key? EVENT_LOAD_ACTION : EVENT_INIT_ACTION,
            type: key? TYPE_LOAD : TYPE_INIT
          }
        ],
        undefined,
        callContext
      );
    }
    else {
      const keyParams:IObject = _.isArray(key)? this.reportFiltersUtil.paramToFilters(key) : undefined;

      return this.createActionRequest(
        undefined,
        key? this.transformParamsToKeySections(keyParams) : (context? this.transformContextToSections(context) : undefined),
        [
          {
            id: key? EVENT_LOAD_ACTION : EVENT_INIT_ACTION,
            type: key? TYPE_LOAD : TYPE_INIT
          }
        ],
        _.isEmpty(keyParams)? { key } : undefined,
        callContext
      );
    }
  }

  /**
   * @param initId {string} - init id of ga3 action
   * @param [context] {IObject} - context of default init model
   * @param [callContext] {IGA3CallContext} - call context of report action
   * @description create init event request for ga3 report action
   * @return {IGAFormActionEventRequest}
   */
  public initReportActionRequest(initId: string, context?:IObject, callContext?: IGA3CallContext):IGAFormActionEventRequest {
    return this.createActionRequest(
      undefined,
      context? this.transformContextToSections(context) : undefined,
      [{ id: initId, type: EVENT_CUSTOM_ACTION}],
      {},
      callContext
    );
  }

  /**
   * @param model {any} - formly model
   * @param sections {IGAFormSection[]} - form sections
   * @param actions {IGAFormAction[]} - form action that needs to be trigger
   * @param context {any} - form context
   * @param callContext {IGA3CallContext} - call context for report action
   * @return {IGAFormActionEventRequest} - form action object to be used on http request
   */
  public createActionRequest(model:any, sections:IGAFormSection[], actions:IGAFormAction[], context?:any, callContext?: IGA3CallContext):IGAFormActionEventRequest {
    const request:IGAFormActionEventRequest = {
      type: ResponseType.FORM,
      actions,
      context: {},
      callContext: _.extend({}, callContext)
    };
    // extend context
    request.context = _.extend(request.context, context);

    if (sections?.length) {
      request.context.fields = _.flatten(
        _.map(sections, (section:IGAFormSection) => {
          return _.map(section.fields, (sectionField:IGAFormField) => {
            return {
              id: sectionField.id,
              type: sectionField.type,
              visibility: sectionField.visibility,
              key: sectionField.key,
              data: model? this.transformModelValue(sectionField.type, model[sectionField.id]) : sectionField.data
            };
          });
        })
      );
    }

    return request;
  }

  /**
   * @param model {any} - formly model
   * @param sections {IGAFormSection[]} - form sections
   * @param sourceField {IGAFormField} - field that trigger event
   * @param filterValue {string} - filter value for search field
   * @param callContext {IGA3CallContext} - ga3 call context
   * @return {IGAFormActionEventRequest} - return event request object to be used on http request
   */
  public createEventRequest(model:any, sections:IGAFormSection[], sourceField:IGAFormField, filterValue?:string, callContext?: IGA3CallContext):IGAFormActionEventRequest {
    const events:IGAFormEvent[] = _.filter(sourceField.events, (event:IGAFormEvent) => !event.when && event.type === EVENT_REQUEST_ITEMS)
      .map((event:IGAFormEvent) => {
        event.source = sourceField.id;
        event.data = _.extend(event.data || {}, { filter: filterValue });
        return event;
      });

    return {
      type: ResponseType.FORM,
      context: {
        fields: _.flatten(
          _.map(sections, (section:IGAFormSection) => {
            return _.map(section.fields, (sectionField:IGAFormField) => {
              return {
                id: sectionField.id,
                type: sectionField.type,
                status: sectionField.status,
                visibility: sectionField.visibility,
                key: sectionField.key,
                data: model? this.transformModelValue(sectionField.type, model[sectionField.id]) : sectionField.data
              };
            });
          })
        )
      },
      callContext,
      events
    };
  }

  /**
   * @param sourceMetadata {IGAFormMetadata} - current metadata
   * @param newMetadata {IGAFormMetadata} - latest metadata
   * @description smart merge object of metadata, update metadata when there is changed in field level object
   * @return {IGAFormMetadata}
   */
  public mergeMetadata(sourceMetadata:IGAFormMetadata, newMetadata:IGAFormMetadata):IGAFormMetadata {
    if (!sourceMetadata) {
      return newMetadata;
    }

    const metadataSections:IGAFormMetadata = util.deepClone(newMetadata);
    _.map(metadataSections.sections, (section:IGAFormSection) => {
      return _.map(section.fields, (sectionField:IGAFormField) => {
        const obsoleteSectionField = _.chain(sourceMetadata.sections)
          .map(s => s.fields)
          .flatten()
          .findWhere({ id: sectionField.id })
          .value();

        sectionField = _.extend(
          obsoleteSectionField,
          sectionField
        );

        return sectionField;
      });
    });

    const metadata = _.extend(sourceMetadata, newMetadata);
    metadata.sections = metadataSections.sections;

    return metadata;
  }

  /**
   * @param sourceMetadata {IGAFormMetadata} - current metadata
   * @param newMetadata {IGAFormMetadata} - metadata to be updated to current metadata
   * @description update current metadata property value with recent metadata, and trigger observable of fieldsOnChange
   * @return {IGAFormMetadata}
   */
  public updateMetadata(sourceMetadata:IGAFormMetadata, newMetadata:IGAFormMetadata):IGAFormMetadata {
    _.each(sourceMetadata.sections, (section:IGAFormSection) => {
      _.each(section.fields, (sectionField:IGAFormField) => {
        // use util.deepClone to remove undefined property
        const recentSectionField = util.deepClone(
          _.chain(newMetadata.sections)
            .map(s => s.fields)
            .flatten()
            .findWhere({ id: sectionField.id })
            .value()
        );

        if (recentSectionField && !_.isEqual(sectionField, recentSectionField)) {
          // if field is different with obsolete response of field metadata then trigger change
          this._field$.next(_.extend(sectionField, recentSectionField, {
            sectionId: section.id,
            event: TYPE_UPDATE
          }));
        }
        // when recent section field doesnt exist in current metadata
        else if (!recentSectionField) {
          // when field is deleted from metadata
          this._field$.next(_.extend(sectionField, {
            sectionId: section.id,
            event: TYPE_DELETE
          }));
        }
      });
    });

    // update source metadata property to use the merged one
    sourceMetadata = this.mergeMetadata(sourceMetadata, newMetadata);

    return sourceMetadata;
  }

  /**
   * @param type {string} - field type
   * @param data {any} - field data
   * @description transform formly model to ga3 model object
   * @return {IGAFormItem}
   */
  public transformModelValue(type:string, data:any):IGAFormItem {
    // if model is undefined then just return null for both data object
    if (util.isNullOrUndefinedOrEmpty(data)) {
      if (this.keyValueFields.indexOf(type) > -1) {
        return {
          keyValue: null,
          displayValue: null
        };
      } else {
        return {
          displayValue: null
        };
      }
    }

    if (type === FIELD_TYPE_DATEINPUT) {
      return {
        displayValue: (_.isNumber(data) ? data : (<Date>data)?.getTime()) || null
      };
    } else if (type === FIELD_TYPE_NUMBER) {
      return {
        displayValue: util.notNullOrUndefined(data)? parseFloat(data) : null
      };
    } else {
      return _.isObject(data)?
        (_.isEmpty(data) ?  { displayValue: null, keyValue: null } : data ) : { displayValue: data };
    }
  }

  /**
   * @param type {string} - field type
   * @param data {IGAFormItem} - field data
   * @param visibility {GA3FormVisibility} - field visibility
   * @description extract field model value
   * @return {any}
   */
  public extractModelValue(type:string, data:IGAFormItem, visibility:GA3FormVisibility):any {
    if (type === FIELD_TYPE_SEARCH || type === FIELD_TYPE_SELECT || type === FIELD_TYPE_SELECT2 || type === FIELD_TYPE_MULTISELECT || type === FIELD_TYPE_RADIO || (type === FIELD_TYPE_INPUT && this.isFieldReadonly(visibility))) {
      return _.isEmpty(data)? undefined : (util.isNullOrUndefined(data?.keyValue) && util.isNullOrUndefined(data?.displayValue)? undefined : data);
    } else {
      return data?.displayValue;
    }
  }

  /**
   * @param metadata {IGAFormMetadata}
   * @description get primary key value to be used in browselist
   * @return {IObject}
   */
  public getKeyValue(metadata:IGAFormMetadata):IObject {
    const keys:IObject = {};

    // first flatten all sections array into single collection of form fields
    _.chain(_.flatten(_.map(metadata.sections, (section:IGAFormSection) => section.fields)))
      .filter((field:IGAFormField) => !!field.key)
      .each((field:IGAFormField) => {
        keys[field.id] = this.getFieldValue(field);
      });

    return keys;
  }

  /**
   * @param field {IGAFormField} - field object
   * @description get field value based on type
   * @return {string | number}
   */
  public getFieldValue(field:IGAFormField):string | number {
    if (field.type === FIELD_TYPE_SEARCH || field.type === FIELD_TYPE_SELECT || field.type === FIELD_TYPE_SELECT2 || field.type === FIELD_TYPE_MULTISELECT || field.type === FIELD_TYPE_RADIO) {
      return util.notNullOrUndefined(field.data?.keyValue)? field.data?.keyValue : field.data?.displayValue;
    } else {
      return field.data?.displayValue;
    }
  }

  /**
   * @param type {string} - field type
   * @param data {IGAFormItem} - field data
   * @description check if data is empty
   * @return {boolean}
   */
  public isValueEmpty(type:string, data:IGAFormItem):boolean {
    if (type === FIELD_TYPE_SEARCH || type === FIELD_TYPE_SELECT || type === FIELD_TYPE_SELECT2 || type === FIELD_TYPE_MULTISELECT || type === FIELD_TYPE_RADIO) {
      return util.isNullOrUndefined(data?.keyValue);
    } else {
      return util.isNullOrUndefined(data?.displayValue);
    }
  }

  /**
   * @param params {IObject} - formatted url query params
   * @description create temporary key sections to be used in FormsService.createActionRequest(...) from URL query parameters
   * @return {IGAFormSection[]}
   */
  public transformParamsToKeySections(params:IObject):IGAFormSection[] {
    if (!params || _.isEmpty(params)) {
      return undefined;
    }

    // create empty field temporary section
    const sections:IGAFormSection[] = [
      {
        id: 'TEMP_SECTION',
        fields: []
      }
    ];

    _.each(params, (value:any, key:string) => {
      sections[0].fields.push({
        id: key,
        key: true,
        data: {
          displayValue: value
        }
      });
    });

    return sections;
  }

  /**
   * @param context {Dictionary<IGAFormTypeValue>} - field context
   * @description create temporary field context sections to be used in FormsService.createActionRequest(...) from input bindings
   * @return {IGAFormSection[]}
   */
  public transformContextToSections(context:Dictionary<IGAFormTypeValue>):IGAFormSection[] {
    if (!context || _.isEmpty(context)) {
      return undefined;
    }

    // create empty field temporary section
    const sections:IGAFormSection[] = [
      {
        id: 'TEMP_SECTION',
        fields: []
      }
    ];

    _.each(context, (ctx:IGAFormTypeValue, key:string) => {
      sections[0].fields.push({
        id: key,
        visibility: _.isUndefined(ctx.visibility) ? GA3FormVisibility.EDITABLE : ctx.visibility, // field defined in context should always be editable (per default if not specified)
        type: ctx.type,
        key: ctx.key
      });

      const lastIndex: number = sections[0].fields.length - 1;
      // if context value is available then set value based on input type
      // if undefined then don't send empty object to server
      if (util.notNullOrUndefinedOrEmpty(ctx.value)) {
        if (this.keyValueFields.indexOf(ctx.type) > -1) {
          sections[0].fields[lastIndex].data = {
            keyValue: ctx.value
          };
        } else {
          sections[0].fields[lastIndex].data = {
            displayValue: ctx.value
          };
        }
      } else {
        if (this.keyValueFields.indexOf(ctx.type) > -1) {
          sections[0].fields[lastIndex].data = {
            keyValue: null,
            displayValue: null
          };
        } else {
          sections[0].fields[lastIndex].data = {
            displayValue: null
          };
        }
      }
    });

    return sections;
  }

  /**
   * @param obj {IGAFormField | GA3FormVisibility} - field object
   * @description is field readonly
   * @return {boolean}
   */
  public isFieldReadonly(obj: IGAFormField | GA3FormVisibility): boolean {
    let visibility: string;

    if (_.isString(obj)) {
      visibility = obj;
    } else {
      visibility = obj.visibility;
    }

    return visibility === GA3FormVisibility.READONLY;
  }

  /**
   * @param field {IGAFormField} - field object
   * @description is field editable
   * @return {boolean}
   */
  public isFieldEditable(field: IGAFormField): boolean {
    return field.visibility === GA3FormVisibility.EDITABLE;
  }

  /**
   * @param field {IGAFormField} - field object
   * @description is field hidden
   * @return {boolean}
   */
  public isFieldHidden(field: IGAFormField): boolean {
    return field.visibility === GA3FormVisibility.HIDDEN;
  }
}